<template>
    <div class="timeline-wrapper">
        <Timeline :value="events" align="left">
            <template #marker="slotProps">
                <span
                    class="dot-wrapper"
                    :class="{
                        'dot-start': slotProps.item.type === 'Start',
                        'dot-end': slotProps.item.type === STATUS.READY,
                        'dot-deploying': slotProps.item.type === STATUS.DEPLOYING,
                        'dot-failed': slotProps.item.type === STATUS.FAILED,
                        'dot-queued': slotProps.item.type === STATUS.QUEUED,
                    }"
                >
                    <span class="dot"> <span class="inner-dot"></span></span>
                </span>
            </template>

            <template #content="slotProps">
                <div :class="slotProps.item.type === 'Start' ? 'event-card-start' : 'event-card'">
                    <p class="event-date">{{ slotProps.item.date }}</p>
                    <div class="event-details"> 
                        <span v-if="slotProps.item.type === 'Start'" class="event-tag">{{localizer.Localize('LandscapeManagement_Start_Tag_Text')}}</span>
                        <span v-else-if="slotProps.item.type === 'Ready'" class="event-tag">{{localizer.Localize('LandscapeManagement_End_Tag_Text')}}</span>
                        <AlmStatusBadge v-else :params="{ value: slotProps.item.type }" />
                        <span class="event-label">{{ slotProps.item.label }}</span>
                    </div>
                </div>
            </template>

            <template #connector="slotProps">
                <div
                    class="p-timeline-event-connector"
                    :style="{ backgroundColor: getConnectorColor(slotProps.item.type) }"
                ></div>
            </template>
        </Timeline>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {Localizer} from '@/api/Localizer';
import AlmStatusBadge from "@/components/pages/landscape/AlmStatusBadge.vue";
import { FormatDateTime } from "@/utils/Utils";
import { STATUS } from "./LandscapeManagementInterfaces";

export default defineComponent({
    components: {
        AlmStatusBadge,
    },
    props:{
        currentTenant: Object
    },
    data() {
        return {
            localizer: new Localizer(this.$store),
            STATUS,
            events: this.mapEvents(),
        };
    },
    watch: {
        currentTenant: {
            handler() {
                this.events = this.mapEvents();
            },
            immediate: true,
        },
    },
    methods: {
        mapEvents() {
            const events = [];
            if (this.currentTenant && this.currentTenant.lastDeployed) {
                events.push({
                    label: this.currentTenant?.tenant,
                    date: this.getFormattedDate(this.currentTenant.lastDeployed),
                    type: this.currentTenant?.status
                },
                {
                    label: this.currentTenant?.tenant,
                    date: this.getFormattedDate(this.currentTenant.deployedAt),
                    type: 'Start'
                });

            }
            return events;
        },
        getFormattedDate(date: Date | string): string {
            return FormatDateTime(date);
        },
        getConnectorColor(type: string): string {
            switch (type) {
                case STATUS.READY:
                    return 'var(--accent-color-green-25)';
                case STATUS.DEPLOYING:
                    return 'var(--accent-color-yellow-25)';
                case STATUS.FAILED:
                    return 'var(--accent-color-red-25)';
                case STATUS.QUEUED:
                    return 'var(--accent-color-concord-05)';
                default:
                    return 'var(--accent-color-green-25)';
            }
        },
    }
});
</script>

<style scoped>
.timeline-wrapper {
    padding: 16px;
}

.dot-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
}

.dot {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
}

.inner-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--background-primary);
}

.dot-start, .dot-end {
    color: var(--accent-color-green-25);
    filter: drop-shadow(0px 0px 8px var(--accent-color-green-25))
        drop-shadow(0px 0px 8px var(--accent-color-green-25));
}
.dot-start .dot, .dot-end .dot {
    background-color: var(--accent-color-green-25);
}

.dot-deploying {
    color: var(--accent-color-yellow-25);
    filter: drop-shadow(0px 0px 8px var(--accent-color-yellow-25))
        drop-shadow(0px 0px 8px var(--accent-color-yellow-25));
}

.dot-deploying .dot {
    background-color: var(--accent-color-yellow-25);
}

.dot-failed {
    color: var(--accent-color-red-25);
    filter: drop-shadow(0px 0px 8px var(--accent-color-red-25))
        drop-shadow(0px 0px 8px var(--accent-color-red-25));
}

.dot-failed .dot {
    background-color: var(--accent-color-red-25);
}

.dot-queued {
    color: var(--accent-color-concord-05);
    filter: drop-shadow(0px 0px 8px var(--accent-color-concord-05))
        drop-shadow(0px 0px 8px var(--accent-color-concord-05));
}

.dot-queued .dot {
    background-color: var(--accent-color-concord-05);
}

.event-card-start {
    display: flex;
    flex-direction: column;
    margin-top: -27px;
}

.event-card {
    display: flex;
    flex-direction: column-reverse;
}

.event-details {
    display: flex;
}

.event-tag{
    border: 1px solid var(--foreground-primary);
    padding: 2px 4px;
    margin-right: 10px;
    font-size: 12px;
    line-height: 12px;
    font-weight: 600;
    align-content: center;
}

.event-label {
    font-weight: 600;
    font-size: 13px;
    margin: 0;
}
.event-date {
    font-size: 13px;
    margin: 0;
}

:deep(.p-timeline.p-timeline-left .p-timeline-event) {
    flex-direction: row;
    justify-content: flex-start;
}

:deep(.p-timeline.p-timeline-left .p-timeline-event-opposite) {
    display: none;
}

:deep(.p-timeline.p-timeline-left .p-timeline-event-content) {
    margin-left: 1rem;
}

:deep(.p-timeline.p-timeline-left .p-timeline-event-marker) {
    margin-left: 0;
}

:deep(.p-timeline-event-connector){
    min-height: 100px;
    width: 4px;
}
</style>
